import React from 'react';

import styles from './Pagination.module.css';

const defaultButton = props => {
  const { children } = props;
  return (
    <button type="button" {...props} className="-btn">
      {children}
    </button>
  );
};

export default function Pagination({
  // Computed
  pages,
  // Props
  page,
  pageSizeOptions,
  pageSize,
  canPrevious,
  canNext,
  onPageChange,
  onPageSizeChange,
  selectorText: rowsSelectorText,
  entityNameText,
  PageButtonComponent,
}) {
  const filterPages = (visiblePages, totalPages) => {
    return visiblePages.filter(p => p === '...' || p <= +totalPages);
  };

  const getVisiblePages = (pageNumber, total) => {
    if (total < 1) return [1];
    if (total < 7) return filterPages([1, 2, 3, 4, 5, 6], total);
    if (pageNumber < 4)
      return filterPages([1, 2, 3, 4, 5, '...', total], total);
    if (total - pageNumber < 4)
      return filterPages(
        [1, '...', total - 4, total - 3, total - 2, total - 1, total],
        total,
      );
    return filterPages(
      [1, '...', pageNumber, pageNumber + 1, pageNumber + 2, '...', total],
      total,
    );
  };

  const getSafePage = pageNumber => {
    let pageNumberToCheck = pageNumber;
    if (Number.isNaN(pageNumberToCheck)) {
      pageNumberToCheck = page;
    }
    return Math.min(Math.max(pageNumberToCheck, 0), pages - 1);
  };

  const changePage = newPage => {
    const safePage = getSafePage(newPage);
    onPageChange(safePage);
  };

  const visiblePages = getVisiblePages(page, pages);

  return (
    <div>
      <div className="Table__pagingWrapper">
        <div className="Table__prevPageWrapper">
          <PageButtonComponent
            className="Table__pageButton"
            onClick={() => {
              if (!canPrevious) return;
              changePage(page - 1);
            }}
            disabled={!canPrevious}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 48 48"
            >
              &lt; g fill=&quot;none&quot; fill-rule=&quot;evenodd&quot;&gt;
              <path fill="none" d="M0 0h48v48H0z" />
              <path
                fill="#000"
                stroke="#2B2A3A"
                d="M30.662 39a.666.666 0 0 1-.46-.183L17.057 26.445A3.322 3.322 0 0 1 16 24c0-.936.375-1.804 1.057-2.445L30.202 9.183a.671.671 0 1 1 .92.978L17.978 22.532c-.409.385-.634.907-.634 1.468 0 .562.225 1.083.634 1.468l13.146 12.371a.672.672 0 0 1-.46 1.161"
              />
            </svg>
          </PageButtonComponent>
        </div>
        <div className="Table__visiblePagesWrapper">
          {visiblePages.map((pageNumber, index) => {
            if (pageNumber === '...')
              return (
                <span key={pageNumber + index.toString()} className="ellipsis">
                  {' '}
                  {pageNumber}{' '}
                </span>
              );
            return (
              <PageButtonComponent
                key={pageNumber + index.toString()}
                className={Pagination.getClassName(page, pageNumber)}
                onClick={() => changePage(pageNumber - 1)}
              >
                {pageNumber}
              </PageButtonComponent>
            );
          })}
        </div>
        <div className="Table__nextPageWrapper">
          <PageButtonComponent
            className="Table__pageButton"
            onClick={() => {
              if (!canNext) return;
              changePage(page + 1);
            }}
            disabled={!canNext}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 48 48"
            >
              &lt; g fill=&quot;none&quot; fill-rule=&quot;evenodd&quot;&gt;
              <path fill="none" d="M0 0h48v48H0z" />
              <path
                fill="#000"
                stroke="#2B2A3A"
                d="M16.671 39a.67.67 0 0 1-.46-1.16l13.145-12.372c.41-.385.634-.906.634-1.468 0-.561-.224-1.083-.634-1.468L16.211 10.161a.672.672 0 1 1 .922-.978l13.144 12.372A3.322 3.322 0 0 1 31.334 24c0 .936-.375 1.805-1.057 2.445L17.133 38.817a.668.668 0 0 1-.462.183"
              />
            </svg>
          </PageButtonComponent>
        </div>
      </div>
      <span className="select-wrap -pageSizeOptions">
        <div className={styles.ddRows}>
          Show{' '}
          <select
            id="dlRows"
            className="ddRows_select"
            aria-label={rowsSelectorText}
            onChange={e => onPageSizeChange(Number(e.target.value))}
            value={pageSize}
          >
            {pageSizeOptions.map(o => (
              <option key={o} value={o}>
                {o === -1 || o === 10000 ? 'All' : o}
              </option>
            ))}
          </select>{' '}
          {pageSize.toString() === '-1' || pageSize.toString() === '10000'
            ? null
            : `${entityNameText || ''} per page`}
        </div>
      </span>
    </div>
  );
}

Pagination.getClassName = (activePage, page) => {
  return activePage === page - 1
    ? 'Table__pageButton Table__pageButton--active'
    : 'Table__pageButton';
};

Pagination.defaultProps = {
  PageButtonComponent: props => {
    const { children } = props;

    return <a {...props}>{children}</a>;
  },
  NextComponent: defaultButton,
  renderPageJump: ({
    onChange,
    value,
    onBlur,
    onKeyPress,
    inputType,
    pageJumpText,
  }) => (
    <div className="-pageJump">
      <input
        aria-label={pageJumpText}
        type={inputType}
        onChange={onChange}
        value={value}
        onBlur={onBlur}
        onKeyPress={onKeyPress}
      />
    </div>
  ),
  renderCurrentPage: page => <span className="-currentPage">{page + 1}</span>,
  renderTotalPagesCount: pages => (
    <span className="-totalPages">{pages || 1}</span>
  ),
};
